import React from 'react';
import { useCall } from './CallProvider';

function formatDuration(seconds) {
  const m = String(Math.floor(seconds / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');
  return `${m}:${s}`;
}

const styles = {
  bar: {
    position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
    zIndex: 9999, display: 'flex', alignItems: 'center', gap: 12,
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    borderRadius: 50, padding: '8px 20px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
    border: '1px solid rgba(255,255,255,0.12)',
    color: '#fff', fontSize: 14, minWidth: 260,
  },
  status: { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 },
  name: { fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  timer: { fontSize: 12, color: 'rgba(255,255,255,0.6)' },
  recDot: {
    width: 8, height: 8, borderRadius: '50%', background: '#ff1744',
    animation: 'vtdBlink 1s infinite', marginRight: 4, display: 'inline-block',
  },
  btn: {
    width: 40, height: 40, borderRadius: '50%', border: 'none',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', color: '#fff', transition: 'opacity .15s',
    flexShrink: 0,
  },
  muteBtn: { background: 'rgba(255,255,255,0.15)' },
  muteBtnActive: { background: '#ff9800' },
  videoBtn: { background: 'rgba(255,255,255,0.15)' },
  videoBtnActive: { background: '#2196f3' },
  recordBtn: { background: 'rgba(255,255,255,0.15)' },
  recordBtnActive: { background: '#ff1744' },
  endBtn: { background: 'linear-gradient(135deg, #ff1744, #ff5252)' },
  connectingBar: {
    position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
    zIndex: 9999, background: 'rgba(26,26,46,0.95)', borderRadius: 50,
    padding: '12px 24px', color: '#fff', fontSize: 14,
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
    border: '1px solid rgba(255,255,255,0.12)',
  },
};

const blink = `@keyframes vtdBlink { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }`;

const MicSvg = ({ muted }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    {muted
      ? <><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .39-.03.77-.1 1.14"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></>
      : <><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></>
    }
  </svg>
);

const VideoSvg = ({ on }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    {on
      ? <><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></>
      : <><line x1="1" y1="1" x2="23" y2="23"/><path d="M15 13a3 3 0 0 1-3 3H7a2 2 0 0 1-2-2V7m4 0h6l4-4v11"/></>
    }
  </svg>
);

const RecordSvg = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <circle cx="12" cy="12" r="8"/>
  </svg>
);

const EndSvg = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.956.956 0 0 1-.29-.7c0-.28.11-.52.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.19.29.44.29.71s-.11.51-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.1-.7-.28-.79-.74-1.69-1.36-2.67-1.85a1.003 1.003 0 0 1-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/>
  </svg>
);

export default function ActiveCallBar({ isRecording, onToggleRecording }) {
  const { callState, callInfo, callDuration, isMuted, isVideoOn, endCall, toggleMute, toggleVideo, CALL_STATES } = useCall();

  if (callState === CALL_STATES.RINGING && callInfo?.direction === 'outgoing') {
    return (
      <>
        <style>{blink}</style>
        <div style={styles.connectingBar}>
          Calling {callInfo.fromName || 'user'}...
          <button style={{ ...styles.btn, ...styles.endBtn, marginLeft: 12, display: 'inline-flex' }} onClick={endCall} title="Cancel">
            <EndSvg />
          </button>
        </div>
      </>
    );
  }

  if (callState !== CALL_STATES.ACTIVE && callState !== CALL_STATES.CONNECTING) return null;

  const peerName = callInfo?.fromName || callInfo?.to || 'Call';

  return (
    <>
      <style>{blink}</style>
      <div style={styles.bar}>
        <div style={styles.status}>
          <span style={styles.name}>{peerName}</span>
          <span style={styles.timer}>
            {isRecording && <span style={styles.recDot} />}
            {callState === CALL_STATES.CONNECTING ? 'Connecting...' : formatDuration(callDuration)}
          </span>
        </div>

        <button
          style={{ ...styles.btn, ...(isMuted ? styles.muteBtnActive : styles.muteBtn) }}
          onClick={toggleMute} title={isMuted ? 'Unmute' : 'Mute'}
        >
          <MicSvg muted={isMuted} />
        </button>

        <button
          style={{ ...styles.btn, ...(isVideoOn ? styles.videoBtnActive : styles.videoBtn) }}
          onClick={toggleVideo} title={isVideoOn ? 'Stop Video' : 'Start Video'}
        >
          <VideoSvg on={isVideoOn} />
        </button>

        {onToggleRecording && (
          <button
            style={{ ...styles.btn, ...(isRecording ? styles.recordBtnActive : styles.recordBtn) }}
            onClick={onToggleRecording} title={isRecording ? 'Stop Recording' : 'Start Recording'}
          >
            <RecordSvg />
          </button>
        )}

        <button style={{ ...styles.btn, ...styles.endBtn }} onClick={endCall} title="End Call">
          <EndSvg />
        </button>
      </div>
    </>
  );
}
